import { ThemedText } from '@/components/themed-text';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useChargesByEnrollment } from '@/hooks/use-charges';
import { Charge } from '@/types/charge';
import { Feather } from '@expo/vector-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, LayoutChangeEvent, TouchableOpacity, View } from 'react-native';
import Animated, {
  runOnUI,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { ChargeItem } from './charge-item';
import { Button } from '@/components/ui/button';

function AnimatedIcon({ isOpen, color }: { isOpen: boolean; color: string }) {
  const rotation = useSharedValue(isOpen ? -180 : 0);
  useEffect(() => {
    rotation.value = withTiming(isOpen ? -180 : 0, { duration: 250 });
  }, [isOpen, rotation]);
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));
  return (
    <Animated.View style={animatedStyle}>
      <Feather name="chevron-down" size={20} color={color} />
    </Animated.View>
  );
}

type ChargeListProps = {
  enrollmentId: string;
  initialOpen?: boolean;
  onChargePress: (charge: Charge) => void;
};

export function ChargeList({ enrollmentId, initialOpen = false, onChargePress }: ChargeListProps) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [page, setPage] = useState(1);
  const [allCharges, setAllCharges] = useState<Charge[]>([]);
  const colorScheme = useColorScheme();
  const iconColor = Colors[colorScheme ?? 'light'].icon;

  const { data, isLoading, isFetching } = useChargesByEnrollment({
    enrollmentId,
    page,
    limit: 10,
  });

  useEffect(() => {
    if (data?.data) {
      if (page === 1) {
        setAllCharges(data.data);
      } else {
        setAllCharges((prev) => {
          const newCharges = data.data.filter((c) => !prev.some((p) => p.id === c.id));
          return [...prev, ...newCharges];
        });
      }
    }
  }, [data, page]);

  const animatedHeight = useSharedValue(0);
  const contentHeight = useSharedValue(0);

  const animatedContentStyle = useAnimatedStyle(() => {
    return {
      height: withTiming(animatedHeight.value, { duration: 250 }),
      overflow: 'hidden',
    };
  });

  const measureContentHeight = useCallback(
    (event: LayoutChangeEvent) => {
      const measuredHeight = event.nativeEvent.layout.height;
      if (measuredHeight > 0 && contentHeight.value !== measuredHeight) {
        runOnUI(() => {
          'worklet';
          contentHeight.value = measuredHeight;
          if (isOpen) {
            animatedHeight.value = measuredHeight;
          }
        })();
      }
    },
    [isOpen, contentHeight, animatedHeight],
  );

  const toggleAccordion = () => {
    const nextIsOpen = !isOpen;
    if (nextIsOpen) {
      runOnUI(() => {
        'worklet';
        animatedHeight.value = contentHeight.value;
      })();
    } else {
      runOnUI(() => {
        'worklet';
        animatedHeight.value = 0;
      })();
    }
    setIsOpen(nextIsOpen);
  };

  useEffect(() => {
    if (isOpen) {
      runOnUI(() => {
        'worklet';
        animatedHeight.value = contentHeight.value;
      })();
    } else {
      runOnUI(() => {
        'worklet';
        animatedHeight.value = 0;
      })();
    }
  }, [isOpen, contentHeight, animatedHeight, allCharges]);

  const hasMore = data ? allCharges.length < data.total : false;
  const isLoadingList = isLoading;

  const handleLoadMore = () => {
    if (hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <View className="mt-5 w-full rounded-xl bg-card p-4 shadow-sm">
      <TouchableOpacity
        onPress={toggleAccordion}
        className="flex-row items-center justify-between"
        activeOpacity={0.7}
        testID="enrollment-charge-list-accordion"
      >
        <ThemedText className="font-semibold text-base text-card-foreground">
          Histórico de Cobranças {data ? `(${data.total})` : ''}
        </ThemedText>
        <AnimatedIcon isOpen={isOpen} color={iconColor} />
      </TouchableOpacity>
      <Animated.View style={animatedContentStyle}>
        <View onLayout={measureContentHeight} className="pt-4">
          {isLoadingList && page === 1 ? (
            <ActivityIndicator color={iconColor} />
          ) : allCharges.length > 0 ? (
            <View>
              {allCharges.map((charge, index) => (
                <ChargeItem
                  key={charge.id}
                  charge={charge}
                  onPress={() => onChargePress(charge)}
                  testID={index === 0 ? 'enrollment-charge-list-item' : undefined}
                />
              ))}

              {hasMore && (
                <Button
                  variant="outline"
                  onPress={handleLoadMore}
                  disabled={isFetching}
                  className="mt-2"
                >
                  {isFetching ? (
                    <ActivityIndicator size="small" color={iconColor} />
                  ) : (
                    <ThemedText className="text-muted-foreground text-sm">
                      Carregar mais
                    </ThemedText>
                  )}
                </Button>
              )}
            </View>
          ) : (
            <View className="items-center py-2">
              <ThemedText className="text-muted-foreground">
                Nenhuma cobrança encontrada.
              </ThemedText>
            </View>
          )}
        </View>
      </Animated.View>
    </View>
  );
}
